import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, SettingsIcon, EditIcon, TrashIcon, XIcon } from 'lucide-react';
import { DataTable, Column } from '../components/DataTable';
import { masterDataSets } from '../data/mockData';
import { MasterDataItem } from '../types';
export function MasterSettingsList() {
  const {
    type
  } = useParams<{
    type: string;
  }>();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState<MasterDataItem | null>(null);
  // Validate type
  if (!type || !masterDataSets[type]) {
    return <div className="p-8 text-center text-gray-500" data-id="element-1244">
        Invalid master data type.
      </div>;
  }
  const data = masterDataSets[type];
  const formatTitle = (str: string) => {
    return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const title = formatTitle(type);
  const columns: Column<MasterDataItem>[] = [{
    key: 'code',
    label: 'Code',
    render: item => <span className="font-medium text-indigo-600" data-id="element-1245">{item.code}</span>
  }, {
    key: 'name',
    label: 'Name',
    render: item => <span className="font-medium text-gray-900" data-id="element-1246">{item.name}</span>
  }, {
    key: 'description',
    label: 'Description',
    render: item => <span className="text-gray-500 truncate max-w-md block" data-id="element-1247">
          {item.description || '—'}
        </span>
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1248">
          {item.status}
        </span>
  }];
  const handleDelete = (item: MasterDataItem) => {
    alert(`${item.name} (${item.code}) has been deleted.`);
    setDeleteConfirm(null);
  };
  return <div className="space-y-6" data-id="element-1249">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1250">
        <div className="flex items-center gap-3" data-id="element-1251">
          <div className="p-2.5 bg-slate-100 rounded-lg border border-slate-200" data-id="element-1252">
            <SettingsIcon className="h-6 w-6 text-slate-600" data-id="element-1253" />
          </div>
          <div data-id="element-1254">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1255">{title}</h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1256">
              Manage master data records for {title.toLowerCase()}.
            </p>
          </div>
        </div>
        <button onClick={() => navigate(`/master-settings/${type}/create`)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1257">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-1258" />
          New {title.endsWith('s') ? title.slice(0, -1) : title}
        </button>
      </div>

      <div className="h-[600px]" data-id="element-1259">
        <DataTable columns={columns} data={data} keyExtractor={item => item.id} searchPlaceholder={`Search ${title.toLowerCase()}...`} actions={item => <div className="flex items-center gap-2 justify-end" data-id="element-1261">
              <button onClick={() => navigate(`/master-settings/${type}/${item.id}/edit`)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-1262">
                <EditIcon className="h-3.5 w-3.5 mr-1" data-id="element-1263" /> Edit
              </button>
              <button onClick={() => setDeleteConfirm(item)} className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100" data-id="element-1264">
                <TrashIcon className="h-3.5 w-3.5 mr-1" data-id="element-1265" /> Delete
              </button>
            </div>} data-id="element-1260" />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1266">
          <div className="flex min-h-full items-center justify-center p-4" data-id="element-1267">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={() => setDeleteConfirm(null)} data-id="element-1268" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md z-10" data-id="element-1269">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1270">
                <div className="flex items-center gap-3" data-id="element-1271">
                  <div className="p-2 bg-red-100 rounded-lg" data-id="element-1272">
                    <TrashIcon className="h-5 w-5 text-red-600" data-id="element-1273" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1274">
                    Confirm Delete
                  </h3>
                </div>
                <button onClick={() => setDeleteConfirm(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100" data-id="element-1275">
                  <XIcon className="h-5 w-5" data-id="element-1276" />
                </button>
              </div>
              <div className="px-6 py-5" data-id="element-1277">
                <p className="text-sm text-gray-600" data-id="element-1278">
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-gray-900" data-id="element-1279">
                    {deleteConfirm.name}
                  </span>{' '}
                  ({deleteConfirm.code})? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg" data-id="element-1280">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1281">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700" data-id="element-1282">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}